import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { COST_METHOD, type CostMethod } from "@/features/finances/types";
import {
  clientInvoicesApi,
  consortiumAccessApi,
  consortiumAgreementsApi,
  jvInvoicesApi,
  type ConsortiumAgreementFormData,
  type JointVentureInvoiceFormData,
  costAllocationApi,
  employeeAllocationsApi,
  employeeExpensesApi,
  globalCostAllocationsApi,
  internalCostInvoiceApi,
  oneTimeCostsApi,
  plannedRevenuesApi,
  projectFinanceApi,
  recurringCostsApi,
  salaryCostsApi,
  propDistExclusionsApi,
} from "@/features/finances/api/finances";

// ── Financial summary & cashflow ──────────────────────────────────────────

export function useFinancialSummary(projectId: string | undefined, method?: CostMethod) {
  return useQuery({
    queryKey: ["projects", projectId, "financial-summary", method ?? COST_METHOD.PROPORTIONAL],
    queryFn: () => projectFinanceApi.summary(projectId!, method),
    enabled: !!projectId,
  });
}

export function useCashflowEvents(projectId: string | undefined, method?: CostMethod) {
  return useQuery({
    queryKey: ["projects", projectId, "cashflow-events", method ?? COST_METHOD.PROPORTIONAL],
    queryFn: () => projectFinanceApi.cashflowEvents(projectId!, method),
    enabled: !!projectId,
  });
}

export function useEngagementCurve(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId, "engagement-curve"],
    queryFn: () => projectFinanceApi.engagementCurve(projectId!),
    enabled: !!projectId,
  });
}

export function useNarzutFixedBreakdown(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["projects", projectId, "narzut-fixed-breakdown"],
    queryFn: () => projectFinanceApi.narzutFixedBreakdown(projectId!),
    enabled: !!projectId && enabled,
  });
}

export function useNarzutPeopleBreakdown(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["projects", projectId, "narzut-people-breakdown"],
    queryFn: () => projectFinanceApi.narzutPeopleBreakdown(projectId!),
    enabled: !!projectId && enabled,
  });
}

export function useUnassignedRecurringBreakdown(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["projects", projectId, "unassigned-recurring-breakdown"],
    queryFn: () => projectFinanceApi.unassignedRecurringBreakdown(projectId!),
    enabled: !!projectId && enabled,
  });
}

export function useRecurringCostsByMonth(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId, "recurring-costs-by-month"],
    queryFn: () => projectFinanceApi.recurringCostsByMonth(projectId!),
    enabled: !!projectId,
  });
}

export function useSalaryCostsByMonth(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId, "salary-costs-by-month"],
    queryFn: () => projectFinanceApi.salaryCostsByMonth(projectId!),
    enabled: !!projectId,
  });
}

// ── Client Invoices ───────────────────────────────────────────────────────

export function useClientInvoices(params?: Record<string, string>) {
  return useQuery({
    queryKey: ["client-invoices", params],
    queryFn: () => clientInvoicesApi.list(params),
    enabled: true,
  });
}

export function useCreateClientInvoice(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: clientInvoicesApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-invoices"] });
      qc.invalidateQueries({ queryKey: ["prz-needs-approval-count"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

export function useUpdateClientInvoicePayment(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof clientInvoicesApi.updatePayment>[1] }) =>
      clientInvoicesApi.updatePayment(id, data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["client-invoices"] });
      qc.invalidateQueries({ queryKey: ["client-invoices", vars.id, "events"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
      qc.invalidateQueries({ queryKey: ["prz-proforma-to-convert-count"] });
      if (projectId) qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
    },
  });
}

export function useUpdateClientInvoice(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof clientInvoicesApi.update>[1] }) =>
      clientInvoicesApi.update(id, data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["client-invoices"] });
      qc.invalidateQueries({ queryKey: ["client-invoices", vars.id, "events"] });
      qc.invalidateQueries({ queryKey: ["planned-revenues"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
      qc.invalidateQueries({ queryKey: ["prz-draft-count"] });
      qc.invalidateQueries({ queryKey: ["prz-investor-count"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
      }
    },
  });
}

export function useClientInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ["client-invoices", id],
    queryFn: () => clientInvoicesApi.get(id!),
    enabled: !!id,
  });
}

/** Ręczne uruchomienie / odświeżenie analizy AI pliku faktury PRZ (Prezes, księgowość). */
export function useAnalyzeClientInvoice(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => clientInvoicesApi.analyze(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-invoices", id] });
    },
  });
}

export function useClientInvoiceEvents(id: string | undefined) {
  return useQuery({
    queryKey: ["client-invoices", id, "events"],
    queryFn: () => clientInvoicesApi.getEvents(id!),
    enabled: !!id,
  });
}

export function useDeleteClientInvoicePaymentEvent(invoiceId: string | undefined, projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => clientInvoicesApi.deletePaymentEvent(invoiceId!, eventId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-invoices"] });
      qc.invalidateQueries({ queryKey: ["client-invoices", invoiceId, "events"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
      if (projectId) qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
    },
  });
}

export function useAddClientInvoiceComment(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ message, mentionIds, files }: { message: string; mentionIds?: string[]; files?: File[] }) =>
      clientInvoicesApi.addComment(id!, message, mentionIds, files),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-invoices", id, "events"] });
    },
  });
}

export function useSetExpectedPaymentDate(invoiceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (date: string | null) => clientInvoicesApi.setExpectedDate(invoiceId!, date),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-invoices", invoiceId] });
      qc.invalidateQueries({ queryKey: ["client-invoices", invoiceId, "events"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
    },
  });
}

export function useApprovePrzDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientInvoicesApi.approve(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["client-invoices"] });
      qc.invalidateQueries({ queryKey: ["client-invoices", id, "events"] });
      qc.invalidateQueries({ queryKey: ["prz-needs-approval-count"] });
      qc.invalidateQueries({ queryKey: ["prz-draft-count"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
    },
  });
}

export function useRevokeApprovalPrzDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientInvoicesApi.revokeApproval(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["client-invoices"] });
      qc.invalidateQueries({ queryKey: ["client-invoices", id, "events"] });
      qc.invalidateQueries({ queryKey: ["prz-needs-approval-count"] });
      qc.invalidateQueries({ queryKey: ["prz-draft-count"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
    },
  });
}

export function useClientInvoiceLineItems(invoiceId: string | undefined) {
  return useQuery({
    queryKey: ["client-invoices", invoiceId, "line-items"],
    queryFn: () => clientInvoicesApi.listLineItems(invoiceId!),
    enabled: !!invoiceId,
  });
}

export function useCreateLineItem(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; unit_value: string; quantity: string }) =>
      clientInvoicesApi.createLineItem(invoiceId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["client-invoices", invoiceId, "line-items"] }),
  });
}

export function useUpdateLineItem(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: Partial<{ name: string; unit_value: string; quantity: string; position: number }> }) =>
      clientInvoicesApi.updateLineItem(invoiceId, itemId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["client-invoices", invoiceId, "line-items"] }),
  });
}

export function useDeleteLineItem(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => clientInvoicesApi.deleteLineItem(invoiceId, itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["client-invoices", invoiceId, "line-items"] }),
  });
}

export function useConvertProforma(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: FormData) => clientInvoicesApi.convertProforma(invoiceId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-invoices"] });
      qc.invalidateQueries({ queryKey: ["client-invoices", invoiceId, "events"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
      qc.invalidateQueries({ queryKey: ["prz-proforma-to-convert-count"] });
    },
  });
}

export function useDeleteClientInvoice(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientInvoicesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-invoices"] });
      qc.invalidateQueries({ queryKey: ["prz-needs-approval-count"] });
      qc.invalidateQueries({ queryKey: ["prz-draft-count"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

// ── One-Time Costs ────────────────────────────────────────────────────────

export function useOneTimeCosts(projectId?: string) {
  return useQuery({
    queryKey: ["one-time-costs", { project: projectId }],
    queryFn: () => oneTimeCostsApi.list(projectId ? { project: projectId } : undefined),
  });
}

export function useCreateOneTimeCost(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: oneTimeCostsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["one-time-costs"] });
      qc.invalidateQueries({ queryKey: ["global-cost-allocations"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

export function useDeleteOneTimeCost(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => oneTimeCostsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["one-time-costs"] });
      qc.invalidateQueries({ queryKey: ["global-cost-allocations"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

export function useUpdateOneTimeCost(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import("@/features/finances/types").InternalCostOneTime> }) =>
      oneTimeCostsApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["one-time-costs"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

export function useMarkOneTimeCostPaid(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paid_at }: { id: string; paid_at: string }) => oneTimeCostsApi.markPaid(id, paid_at),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["one-time-costs"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

// ── Recurring Costs ───────────────────────────────────────────────────────

export function useRecurringCosts(projectId?: string) {
  return useQuery({
    queryKey: ["recurring-costs", { project: projectId }],
    queryFn: () => recurringCostsApi.list(projectId ? { project: projectId } : undefined),
  });
}

/** Recurring costs still missing their invoice for a month (badge + panel). */
export function useMissingRecurringInvoices(period?: { year: number; month: number }) {
  return useQuery({
    queryKey: ["recurring-costs", "missing-invoices", period ?? null],
    queryFn: () => recurringCostsApi.missingInvoices(period),
  });
}

export function useSkipRecurringMonth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string; period_year: number; period_month: number; note?: string;
    }) => recurringCostsApi.skipMonth(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recurring-costs"] }),
  });
}

export function useUnskipRecurringMonth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; period_year: number; period_month: number }) =>
      recurringCostsApi.unskipMonth(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recurring-costs"] }),
  });
}

export function useCreateRecurringCost(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: recurringCostsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-costs"] });
      qc.invalidateQueries({ queryKey: ["global-cost-allocations"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

export function useUpdateRecurringCost(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import("@/features/finances/types").InternalCostRecurring> }) =>
      recurringCostsApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-costs"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

export function useDeleteRecurringCost(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => recurringCostsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-costs"] });
      qc.invalidateQueries({ queryKey: ["global-cost-allocations"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

// ── Salary Costs ──────────────────────────────────────────────────────────

export function useSalaryCosts() {
  return useQuery({
    queryKey: ["salary-costs"],
    queryFn: () => salaryCostsApi.list(),
  });
}

export function useCreateSalaryCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: salaryCostsApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["salary-costs"] }),
  });
}

export function useDeleteSalaryCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => salaryCostsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["salary-costs"] }),
  });
}

export function useUpdateSalaryCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import("@/features/finances/types").EmployeeSalaryCost> }) =>
      salaryCostsApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["salary-costs"] }),
  });
}

export function useSetSalaryCostPrimary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => salaryCostsApi.setPrimary(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["salary-costs"] }),
  });
}

// ── Employee Allocations ──────────────────────────────────────────────────

export function useEmployeeAllocations(params?: { project?: string; employee?: string; year?: number; month?: number }) {
  const queryParams = Object.fromEntries(
    Object.entries(params ?? {}).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
  );
  return useQuery({
    queryKey: ["employee-allocations", queryParams],
    queryFn: () => employeeAllocationsApi.list(queryParams),
  });
}

export function useCreateEmployeeAllocation(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: employeeAllocationsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-allocations"] });
      if (projectId) qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
    },
  });
}

export function useDeleteEmployeeAllocation(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => employeeAllocationsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-allocations"] });
      if (projectId) qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
    },
  });
}

// ── Proportional Distribution Exclusions ─────────────────────────────────

export function usePropDistExclusions(params: { employee: string; year: number; month: number } | null) {
  return useQuery({
    queryKey: ["prop-dist-exclusions", params],
    queryFn: () => propDistExclusionsApi.list(params!),
    enabled: !!params,
    staleTime: 0,
  });
}

export function useCreatePropDistExclusion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: propDistExclusionsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prop-dist-exclusions"] });
      qc.refetchQueries({ queryKey: ["prop-dist-exclusions"] });
    },
  });
}

export function useDeletePropDistExclusion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: propDistExclusionsApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prop-dist-exclusions"] });
      qc.refetchQueries({ queryKey: ["prop-dist-exclusions"] });
    },
  });
}

// ── Planned Revenues ──────────────────────────────────────────────────────

export function usePlannedRevenues(projectId?: string) {
  return useQuery({
    queryKey: ["planned-revenues", { project: projectId }],
    queryFn: () =>
      plannedRevenuesApi.list(projectId ? { project: projectId, page_size: 200 } : { page_size: 200 }),
    select: (d) => d.items,
  });
}

export function usePaginatedPlannedRevenues(params: import("@/features/finances/api/finances").PlannedRevenueParams) {
  return useQuery({
    queryKey: ["planned-revenues-paginated", params],
    queryFn: () => plannedRevenuesApi.list(params),
  });
}

export function useCreatePlannedRevenue(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: plannedRevenuesApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planned-revenues"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

export function useUpdatePlannedRevenue(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof plannedRevenuesApi.update>[1] }) =>
      plannedRevenuesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planned-revenues"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

export function useDeletePlannedRevenue(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => plannedRevenuesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planned-revenues"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      }
    },
  });
}

export function useConvertPlannedRevenueToDraft(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isAdvance }: { id: string; isAdvance?: boolean }) =>
      plannedRevenuesApi.convertToDraft(id, { is_advance_invoice: isAdvance }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planned-revenues"] });
      qc.invalidateQueries({ queryKey: ["client-invoices"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
      }
    },
  });
}

export function usePlannedRevenuesBySubtask(subtaskId: string | undefined) {
  return useQuery({
    queryKey: ["planned-revenues", "by-subtask", subtaskId],
    queryFn: () => plannedRevenuesApi.listBySubtask(subtaskId!),
    enabled: Boolean(subtaskId),
  });
}

// ── Global Cost Allocations ───────────────────────────────────────────────

export function useGlobalCostAllocationSummary() {
  return useQuery({
    queryKey: ["global-cost-allocations"],
    queryFn: globalCostAllocationsApi.summary,
  });
}

export function useBulkSetGlobalCostAllocations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: globalCostAllocationsApi.bulkSet,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["global-cost-allocations"] }),
  });
}

export function useResetGlobalCostAllocations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: globalCostAllocationsApi.reset,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["global-cost-allocations"] }),
  });
}

// ── Per-Cost Allocations ──────────────────────────────────────────────────

export function useOneTimeCostAllocations(costId: string | undefined) {
  return useQuery({
    queryKey: ["one-time-cost-allocations", costId],
    queryFn: () => costAllocationApi.getForOneTime(costId!),
    enabled: !!costId,
  });
}

export function useSetOneTimeCostAllocations(costId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (allocations: { project: string; fraction: number }[]) =>
      costAllocationApi.setForOneTime(costId, allocations),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["one-time-cost-allocations", costId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useResetOneTimeCostAllocations(costId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => costAllocationApi.resetForOneTime(costId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["one-time-cost-allocations", costId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useRecurringCostAllocations(costId: string | undefined) {
  return useQuery({
    queryKey: ["recurring-cost-allocations", costId],
    queryFn: () => costAllocationApi.getForRecurring(costId!),
    enabled: !!costId,
  });
}

export function useSetRecurringCostAllocations(costId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (allocations: { project: string; fraction: number }[]) =>
      costAllocationApi.setForRecurring(costId, allocations),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-cost-allocations", costId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useResetRecurringCostAllocations(costId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => costAllocationApi.resetForRecurring(costId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-cost-allocations", costId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

// ── Project Status ────────────────────────────────────────────────────────

export function useSetProjectStatus(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof projectFinanceApi.setStatus>[1]) =>
      projectFinanceApi.setStatus(projectId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
    },
  });
}

export function useUpdateProjectValue(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof projectFinanceApi.updateValue>[1]) =>
      projectFinanceApi.updateValue(projectId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["planned-revenues"] });
      qc.invalidateQueries({ queryKey: ["projects", projectId, "cashflow-events"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
    },
  });
}

// ── Employee Expenses ─────────────────────────────────────────────────────

/** An expense action can change a monthly-invoice file, which is shown both in the
 *  flat expense list AND under its recurring cost (/costs + the monthly group), so
 *  invalidate both keys to keep statuses/averages live. */
function invalidateExpensesAndRecurring(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["employee-expenses"] });
  qc.invalidateQueries({ queryKey: ["recurring-costs"] });
}

export function useEmployeeExpenses() {
  return useQuery({
    queryKey: ["employee-expenses"],
    queryFn: employeeExpensesApi.list,
  });
}

export function useExpenseCounts(enabled = true) {
  return useQuery({
    // Shared key with useEmployeeExpenses — one request, two subscribers.
    queryKey: ["employee-expenses"],
    queryFn: employeeExpensesApi.list,
    enabled,
    select: (expenses) => ({
      pendingAcceptance: expenses.filter((e) => e.status === "pending").length,
      pendingPayment: expenses.filter((e) => e.status === "approved").length,
      pendingBooking: expenses.filter(
        (e) => !e.is_booked && e.status !== "rejected" && e.expense_type !== "proforma",
      ).length,
    }),
  });
}

export function useCreateEmployeeExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: employeeExpensesApi.create,
    onSuccess: () => invalidateExpensesAndRecurring(qc),
  });
}

export function useUpdateEmployeeExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof employeeExpensesApi.update>[1] }) =>
      employeeExpensesApi.update(id, data),
    onSuccess: () => invalidateExpensesAndRecurring(qc),
  });
}

export function useEmployeeExpenseProxyTargets(enabled = true) {
  return useQuery({
    queryKey: ["employee-expense-proxy-targets"],
    queryFn: () => employeeExpensesApi.proxyTargets(),
    enabled,
    staleTime: 1000 * 60 * 5,
  });
}

export function useDeleteEmployeeExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => employeeExpensesApi.delete(id),
    onSuccess: () => invalidateExpensesAndRecurring(qc),
  });
}

export function useApproveEmployeeExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      employeeExpensesApi.approve(id, comment),
    onSuccess: () => invalidateExpensesAndRecurring(qc),
  });
}

export function useRejectEmployeeExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      employeeExpensesApi.reject(id, comment),
    onSuccess: () => invalidateExpensesAndRecurring(qc),
  });
}

export function usePrepareTransferEmployeeExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => employeeExpensesApi.prepareTransfer(id),
    onSuccess: () => invalidateExpensesAndRecurring(qc),
  });
}

export function useMarkPaidEmployeeExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paid_at }: { id: string; paid_at: string }) =>
      employeeExpensesApi.markPaid(id, paid_at),
    onSuccess: () => invalidateExpensesAndRecurring(qc),
  });
}

export function useMarkBookedEmployeeExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => employeeExpensesApi.markBooked(id),
    onSuccess: () => invalidateExpensesAndRecurring(qc),
  });
}

export function useEmployeeExpenseHistory(id: string | null, enabled = true) {
  return useQuery({
    queryKey: ["employee-expenses", id, "history"],
    queryFn: () => employeeExpensesApi.history(id as string),
    enabled: enabled && !!id,
  });
}

export function useUndoLastEmployeeExpenseEvent() {
  const qc = useQueryClient();
  return useMutation({
    // Invalidating ["employee-expenses"] also refreshes any open history query
    // (TanStack Query matches query keys by prefix).
    mutationFn: (id: string) => employeeExpensesApi.undoLast(id),
    onSuccess: () => invalidateExpensesAndRecurring(qc),
  });
}

export function useLinkExpenseToRecurringCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string;
      recurring_cost: string;
      period_year: number;
      period_month: number;
    }) => employeeExpensesApi.linkRecurringCost(id, data),
    onSuccess: () => invalidateExpensesAndRecurring(qc),
  });
}

export function useUnlinkExpenseFromRecurringCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => employeeExpensesApi.unlinkRecurringCost(id),
    onSuccess: () => invalidateExpensesAndRecurring(qc),
  });
}

// ── Monthly recurring invoices (Faktury comiesięczne → recurring costs) ────

/** Shared history of all monthly-invoice files of a recurring cost. */
export function useRecurringCostExpenseHistory(id: string | null, enabled = true) {
  return useQuery({
    queryKey: ["recurring-costs", id, "expense-history"],
    queryFn: () => recurringCostsApi.expenseHistory(id as string),
    enabled: enabled && !!id,
  });
}

// ── Internal Cost Invoices ────────────────────────────────────────────────

export function useUploadOneTimeCostInvoice(projectParam?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ costId, file }: { costId: string; file: File }) =>
      internalCostInvoiceApi.uploadForOneTime(costId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["one-time-costs", projectParam] });
      qc.invalidateQueries({ queryKey: ["one-time-costs"] });
    },
  });
}

export function useDeleteOneTimeCostInvoice(projectParam?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (costId: string) => internalCostInvoiceApi.deleteForOneTime(costId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["one-time-costs", projectParam] });
      qc.invalidateQueries({ queryKey: ["one-time-costs"] });
    },
  });
}

export function useRecurringCostInvoices(costId: string | undefined) {
  return useQuery({
    queryKey: ["recurring-cost-invoices", costId],
    queryFn: () => internalCostInvoiceApi.listForRecurring(costId!),
    enabled: !!costId,
  });
}

export function useUploadRecurringCostInvoice(costId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ month, file }: { month: string; file: File }) =>
      internalCostInvoiceApi.uploadForRecurring(costId, month, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recurring-cost-invoices", costId] }),
  });
}

export function useDeleteRecurringCostInvoice(costId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (month: string) => internalCostInvoiceApi.deleteForRecurring(costId, month),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recurring-cost-invoices", costId] }),
  });
}

// ── Joint venture / consortium ─────────────────────────────────────────────

export function useSetInvoiceConsortium(clientInvoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (consortiumAgreement: string | null) =>
      clientInvoicesApi.setConsortium(clientInvoiceId, consortiumAgreement),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-invoices", clientInvoiceId] });
      qc.invalidateQueries({ queryKey: ["client-invoices"] });
    },
  });
}

export function useJvInvoices(clientInvoiceId: string | undefined) {
  return useQuery({
    queryKey: ["client-invoices", clientInvoiceId, "jv-invoices"],
    queryFn: () => jvInvoicesApi.listByInvoice(clientInvoiceId!),
    enabled: !!clientInvoiceId,
  });
}

export function useAllJvInvoices(enabled = true) {
  return useQuery({ queryKey: ["jv-invoices", "all"], queryFn: () => jvInvoicesApi.listAll(), enabled });
}

export function useJvAction() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["jv-invoices"] });
    qc.invalidateQueries({ queryKey: ["client-invoices"] });
  };
  return {
    approve: useMutation({ mutationFn: (id: string) => jvInvoicesApi.approve(id), onSuccess: invalidate }),
    reject: useMutation({ mutationFn: (id: string) => jvInvoicesApi.reject(id), onSuccess: invalidate }),
    prepareTransfer: useMutation({ mutationFn: (id: string) => jvInvoicesApi.prepareTransfer(id), onSuccess: invalidate }),
    markPaid: useMutation({
      mutationFn: ({ id, paidAt }: { id: string; paidAt?: string }) => jvInvoicesApi.markPaid(id, paidAt),
      onSuccess: invalidate,
    }),
    markBooked: useMutation({ mutationFn: (id: string) => jvInvoicesApi.markBooked(id), onSuccess: invalidate }),
  };
}

function useJvInvalidate(clientInvoiceId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["client-invoices", clientInvoiceId, "jv-invoices"] });
    qc.invalidateQueries({ queryKey: ["client-invoices", clientInvoiceId] });
    qc.invalidateQueries({ queryKey: ["client-invoices"] });
  };
}

export function useCreateJvInvoice(clientInvoiceId: string) {
  const invalidate = useJvInvalidate(clientInvoiceId);
  return useMutation({
    mutationFn: (data: JointVentureInvoiceFormData & { invoice_file?: File | null }) => jvInvoicesApi.create(data),
    onSuccess: invalidate,
  });
}

export function useUpdateJvInvoice(clientInvoiceId: string) {
  const invalidate = useJvInvalidate(clientInvoiceId);
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<JointVentureInvoiceFormData> & { invoice_file?: File | null; remove_file?: boolean } }) =>
      jvInvoicesApi.update(id, data),
    onSuccess: invalidate,
  });
}

export function useDeleteJvInvoice(clientInvoiceId: string) {
  const invalidate = useJvInvalidate(clientInvoiceId);
  return useMutation({ mutationFn: (id: string) => jvInvoicesApi.remove(id), onSuccess: invalidate });
}

export function useSubmitJvInvoice(clientInvoiceId: string) {
  const invalidate = useJvInvalidate(clientInvoiceId);
  return useMutation({ mutationFn: (id: string) => jvInvoicesApi.submit(id), onSuccess: invalidate });
}

export function useApproveJvInvoice(clientInvoiceId: string) {
  const invalidate = useJvInvalidate(clientInvoiceId);
  return useMutation({ mutationFn: (id: string) => jvInvoicesApi.approve(id), onSuccess: invalidate });
}

export function useMarkJvInvoicePaid(clientInvoiceId: string) {
  const invalidate = useJvInvalidate(clientInvoiceId);
  return useMutation({ mutationFn: (id: string) => jvInvoicesApi.markPaid(id), onSuccess: invalidate });
}

export function useConsortiumAgreements(activeOnly = false, enabled = true) {
  return useQuery({
    queryKey: ["consortium-agreements", activeOnly],
    queryFn: () => consortiumAgreementsApi.list(activeOnly),
    enabled,
  });
}

export function useCreateConsortiumAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ConsortiumAgreementFormData) => consortiumAgreementsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["consortium-agreements"] }),
  });
}

export function useUpdateConsortiumAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ConsortiumAgreementFormData> }) =>
      consortiumAgreementsApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["consortium-agreements"] }),
  });
}

export function useDeleteConsortiumAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => consortiumAgreementsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["consortium-agreements"] }),
  });
}

export function useConsortiumAccessUsers() {
  return useQuery({ queryKey: ["consortium-access", "users"], queryFn: () => consortiumAccessApi.users() });
}

export function useConsortiumAccessGrants() {
  return useQuery({ queryKey: ["consortium-access", "grants"], queryFn: () => consortiumAccessApi.list() });
}

export function useGrantConsortiumAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => consortiumAccessApi.grant(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["consortium-access"] }),
  });
}

export function useRevokeConsortiumAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) => consortiumAccessApi.revoke(grantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["consortium-access"] }),
  });
}
